import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import useDelayedUnmount from '../../../hooks/useDelayedUnmount';

import './HeaderSheet.scss';

// The scrim left showing under the panel, to tap it shut on — .panel's max-height in the SCSS
const SCRIM_STRIP_REM = 3;

let nextOwnerId = 0;

const release = (body) => {
  body.style.minHeight = '';
  delete body.dataset.sheetOwner;
};

/**
 * A panel that drops down from under a Modal's header and covers its body, for controls a header
 * button keeps out of the body until asked for. Passed to Modal as `sheet`, which hangs it off the
 * header. The shell only: the caller fills it and owns whether it is open.
 */
function HeaderSheet({ isOpen, onClose, label, className, children }) {
  const mounted = useDelayedUnmount(isOpen);
  const rootRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [ownerId] = useState(() => String((nextOwnerId += 1)));

  // The room between the header and the dialog's foot, which CSS can't read from the header alone.
  // Offsets rather than client rects, so the builder's zoom doesn't enter into it.
  useLayoutEffect(() => {
    const header = rootRef.current?.parentElement;
    const frame = header?.parentElement;

    if (!mounted || !frame) return undefined;

    const measure = () => setHeight(frame.clientHeight - header.offsetTop - header.offsetHeight);
    const observer = new ResizeObserver(measure);

    measure();
    observer.observe(frame);
    observer.observe(header);

    return () => observer.disconnect();
  }, [mounted]);

  // A dialog shorter than the sheet grows to hold it, up to its own max-height, by holding its body
  // open underneath; past that the panel scrolls. A body already scrolling is at the cap, and is
  // left alone so its scroll position survives. Eased both ways, and let go once the sheet is gone.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const body = root?.parentElement?.parentElement?.querySelector(':scope > .body');

    if (!mounted || !body) return undefined;

    if (!isOpen) {
      // Left alone if a sibling sheet opened in the same tap and has already claimed the body
      if (body.dataset.sheetOwner === ownerId) release(body);

      return undefined;
    }

    if (body.scrollHeight > body.clientHeight) return undefined;

    const panelBody = root.querySelector('.panel-body');
    const grow = () => {
      const strip = SCRIM_STRIP_REM * parseFloat(getComputedStyle(document.documentElement).fontSize);

      // The panel's bottom border, and a pixel of slack for rounding
      body.style.minHeight = `${panelBody.offsetHeight + strip + 2}px`;
    };
    const observer = new ResizeObserver(grow);

    body.dataset.sheetOwner = ownerId;
    body.style.transition = 'min-height var(--mg-open-duration) var(--mg-open-ease)';
    grow();
    observer.observe(panelBody);

    return () => observer.disconnect();
  }, [mounted, isOpen, ownerId]);

  // Unmounted, the body is handed back as it was found
  useLayoutEffect(() => {
    const body = rootRef.current?.parentElement?.parentElement?.querySelector(':scope > .body');

    if (!mounted || !body) return undefined;

    return () => {
      if (body.dataset.sheetOwner !== ownerId) return;

      release(body);
      body.style.transition = '';
    };
  }, [mounted, ownerId]);

  // Capture phase, so Escape closes the sheet before the dialog under it sees it
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      onClose();
    };

    window.addEventListener('keydown', handleEscape, true);

    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  return (
    <div
      ref={rootRef}
      className={cx('HeaderSheet', className, { 'is-closing': !isOpen })}
      style={height == null ? undefined : { height }}
    >
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div className="panel" role="dialog" aria-label={label}>
        <div className="panel-body">{children}</div>
      </div>
    </div>
  );
}

HeaderSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** The dialog's accessible name */
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default HeaderSheet;
